import EllipsisOutlined from "@ant-design/icons/EllipsisOutlined";
import {
  SUB_GROUP_INVITATION_ACTIVE_STATUS,
  SUB_GROUP_INVITATION_INACTIVE_STATUS,
} from "common/constants";
import { useState } from "react";
import InvitationModal from "features/admin/group/InvitationModal/InvitationModal";
import { copyCodeToClipboard, openMessage } from "utils/misc";
import Dropdown from "common/Dropdown/Dropdown";
import { Invitation, LanguageNameSpaces } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import {
  ACTIVATE_INVITATION_CACHE_KAY,
  DEACTIVATE_INVITATION_CACHE_KAY,
  DELETE_INVITATION_CACHE_KAY,
} from "features/admin/constants";
import { ItemType } from "antd/es/menu/interface";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTheme } from "styled-components";
import RowActionsTrigger from "features/admin/NewLook/RowActionsTrigger";

interface ActionsDropdownProps {
  invitation: Invitation;
  subGroupID: number;
}

const ActionsDropdown = ({ invitation, subGroupID }: ActionsDropdownProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const { isNewLook } = useTheme();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const deleteInvitation =
    sharedLib.sharedApi.admin.useDeleteInvitationMutation({
      fixedCacheKey: DELETE_INVITATION_CACHE_KAY,
    })[0];
  const activateInvitation =
    sharedLib.sharedApi.admin.useActivateInvitationMutation({
      fixedCacheKey: ACTIVATE_INVITATION_CACHE_KAY,
    })[0];
  const deactivateInvitation =
    sharedLib.sharedApi.admin.useDeactivateInvitationMutation({
      fixedCacheKey: DEACTIVATE_INVITATION_CACHE_KAY,
    })[0];

  const handleDeleteInvitation = () => {
    deleteInvitation({
      sub_group_id: subGroupID,
      invitation_id: invitation.id,
    })
      .unwrap()
      .then(() =>
        openMessage(t("invitations.actions.deleteSuccess"), "success")
      )
      .catch((error) =>
        openMessage(
          error.message ? error.message : t("invitations.actions.deleteError"),
          "error"
        )
      );
  };

  const handleActivateInvitation = () => {
    activateInvitation({
      sub_group_id: subGroupID,
      invitation_id: invitation.id,
    })
      .unwrap()
      .then(() =>
        openMessage(t("invitations.actions.activateSuccess"), "success")
      )
      .catch((error) =>
        openMessage(
          error.message
            ? error.message
            : t("invitations.actions.activateError"),
          "error"
        )
      );
  };

  const handleDeactivateInvitation = () => {
    deactivateInvitation({
      sub_group_id: subGroupID,
      invitation_id: invitation.id,
    })
      .unwrap()
      .then(() =>
        openMessage(t("invitations.actions.deactivateSuccess"), "success")
      )
      .catch((error) =>
        openMessage(
          error.message
            ? error.message
            : t("invitations.actions.deactivateError"),
          "error"
        )
      );
  };

  const invitationFullyUsed =
    invitation.num_users &&
    invitation?.accepted_by?.length === invitation.num_users;

  let menuItems: ItemType[] = [
    {
      key: "CopyLink",
      label: t("invitations.actions.copyLink"),
      onClick: () => {
        copyCodeToClipboard(invitation.invitation_link);
        openMessage(t("invitations.actions.copySuccess"), "info");
      },
    },
    {
      key: "Edit",
      label: t("invitations.actions.edit"),
      onClick: () => setEditModalOpen(true),
    },
  ];
  if (
    invitation.status === SUB_GROUP_INVITATION_ACTIVE_STATUS &&
    !invitationFullyUsed
  )
    menuItems.push({
      key: "Deactivate",
      label: t("invitations.actions.deactivate"),
      onClick: () => handleDeactivateInvitation(),
      className: isNewLook ? undefined : "warning",
    });
  if (
    invitation.status === SUB_GROUP_INVITATION_INACTIVE_STATUS &&
    !invitationFullyUsed
  )
    menuItems.push({
      key: "Activate",
      label: t("invitations.actions.activate"),
      onClick: () => handleActivateInvitation(),
      className: isNewLook ? undefined : "success",
    });
  if (invitation?.accepted_by?.length === 0)
    menuItems.push({
      key: "Delete",
      label: t("invitations.actions.delete"),
      onClick: () => handleDeleteInvitation(),
      danger: true,
      className: "danger",
    });

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown title="Actions" menu={{ items: menuItems }} trigger={["click"]}>
        {isNewLook ? (
          <RowActionsTrigger />
        ) : (
          <EllipsisOutlined
            style={{
              cursor: "pointer",
              fontSize: "30px",
              transform: "rotate(90deg)",
            }}
          />
        )}
      </Dropdown>
      {editModalOpen && (
        <div
          onClick={(e) => e.stopPropagation()} // Prevent expanding table rows
        >
          <InvitationModal
            subGroupID={subGroupID}
            closeModal={() => setEditModalOpen(false)}
            invitation={invitation}
          />
        </div>
      )}
    </div>
  );
};

export default ActionsDropdown;
